import {
  Injectable,
  Renderer2,
  RendererFactory2,
  DOCUMENT,
  inject,
  DestroyRef,
} from '@angular/core';

import {
  TranslationUpdateCheckerService,
  updateCheckResponse,
} from './translation_update_checker.service';
import { TranslationLookupService } from './translation_lookup.service';
import * as Sentry from '@sentry/browser';
import { UserService } from '../user.service';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import { Company, User } from '@app/generated/graphql';
import { Subject } from 'rxjs';
import { UserHelper } from '@app/core/helpers/user.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class TranslatorService {
  private _destroyRef = inject(DestroyRef);
  private translationUpdateChecker = inject(TranslationUpdateCheckerService);
  private translationLookup = inject(TranslationLookupService);
  private userService = inject(UserService);
  private rendererFactory = inject(RendererFactory2);
  private document = inject<Document>(DOCUMENT);

  // Variables to be set
  private defaultLocal: string = 'en'; // Default locale to fall back on
  public excludeTags: string[] = []; // Attributes to exclude from translation
  public excludeTagNodeChildren: string[] = []; // Attributes to exclude from translation, do not search deeper

  // Other operating variables
  private isTranslating: boolean = false; // Track state of translation
  private isBuildingTranslations: boolean = false; // Track state of translation building
  private translationDictionary: Record<string, string> = {}; // Cached translations
  private currentLocale: string;
  private _translationVersion: string;
  private renderer: Renderer2;
  private translationRequest$ = new Subject<string[]>();
  private prevUntranslatedText: string[] = [];
  private DEBOUNCE_TIME: number = 300;
  // Local storage keys
  private _lsLanguageCodeKey: string = 'savedLocale';
  private _lsCurrentTranslationVersionKey: string = 'translationVersion'; // key to the last updatedAt that has been saved
  // Translation attributes to be set
  private _nodeTranslated: string = 'data-translated';
  private _doNotTranslate: string = 'do-not-translate';
  private _translationWidgetId: string = 'translation-widget-id'; // do not translate this widget
  // AA defualts
  private userLocale: string;
  private companyDefaultLocale: string;

  constructor() {
    const rendererFactory = this.rendererFactory;

    // Create a renderer instance for this service
    this.renderer = rendererFactory.createRenderer(null, null);
  }
  // private async init(): Promise<void> {
  public init(): void {
    this.setLocaleDefaults();
    this.getCurrentLocale();

    if (this.currentLocale == 'en' || !this.isAgrigateUser()) {
      return;
    }

    // setup localisations from cache
    this._showLoadingWidget('Setting up translations');
    this.getCurrentTranslationVersion();
    try {
      this.translationUpdateChecker
        .checkForUpdates(this.currentLocale, this._translationVersion)
        .pipe(
          tap((res: updateCheckResponse) => {
            if (res.update) {
              console.log(
                'TranslatorService: Translation updates found',
                res.updatedAt,
              );
              this.setLocalDictionary(res.translations, res.updatedAt);
            } else {
              console.log('TranslatorService: No translation updates found');
            }
          }),
          takeUntilDestroyed(this._destroyRef),
        )
        .subscribe((res: updateCheckResponse) => {
          this._getLocalDictionary();

          // setup observer
          this.translate();
          this.observeDOM();

          this._hideLoadingWidget();
          return;
        });
    } catch (err) {
      console.error(
        'TranslatorService: Error checking for updates. \n Error:',
        err,
      );
    }

    this.translationRequest$
      .pipe(
        debounceTime(this.DEBOUNCE_TIME),
        distinctUntilChanged((prev, curr) => {
          const currSet = new Set(curr);
          const prevSet = new Set(prev);
          return ![...prevSet].some((item) => !currSet.has(item));
        }),
        switchMap((untranslatedText) => {
          const combinedSet = new Set([
            ...this.prevUntranslatedText,
            ...untranslatedText,
          ]);
          this.prevUntranslatedText = Array.from(combinedSet);
          return this.translationLookup.lookupAndTranslate(
            Array.from(combinedSet),
            this.currentLocale,
          );
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((newTranslations) => {
        this.updateLocalDictionary(
          newTranslations.translations,
          newTranslations.updatedAt,
        );
        this.isBuildingTranslations = false;
        this.translate();
        if (!this.isTranslating && !this.isBuildingTranslations) {
          this._hideLoadingWidget();
        }
        this.prevUntranslatedText = [];
      });
  }

  private isAgrigateUser() {
    const userData: User | null = this.userService.getUserValue();
    return UserHelper.isAgrigateUser(userData);
  }

  // Set user defualts (AA)
  private setLocaleDefaults() {
    const userData: User | null = this.userService.getUserValue();
    const companyData: Company | null = this.userService.getCompanyValue();
    this.userLocale = userData?.locale;
    this.companyDefaultLocale = companyData?.defaultLocale;
  }

  //#region getCurrentLocale
  /**
   * A method to fetch the current locale
   * @returns string
   */
  public getCurrentLocale(): string {
    // // Check cookies for a saved locale
    // // Check local storage for a saved locale
    // const lsLocale = localStorage.getItem(this._lsLanguageCodeKey);
    // if (lsLocale) {
    //   return this.setCurrentLocale(lsLocale);
    // }

    // Get default locale
    const defaultLocale: string = this._getDefaultLocale();
    // set local storage locale or locale cookie
    return this.setCurrentLocale(defaultLocale);
  }
  //#endregion

  //#region Get Default Locale
  /**
   * A method to manage the collection of the default locale.
   * @returns The default locale string (e.g., 'en')
   *
   * Priority:
   * - User default
   * - Company default
   * - Global default
   */
  private _getDefaultLocale(): string {
    // Check user and company setup for default locale
    const defaultUserLocale: string = this.userLocale;
    const defaultCompanyLocale: string = this.companyDefaultLocale;

    // use available locale
    const defaultLocale: string =
      defaultUserLocale || defaultCompanyLocale || this.defaultLocal;

    return defaultLocale;
  }
  //#endregion

  //#region setCurrentLocale
  /**
   * A method to set the current locale
   * @returns locale set as string
   */
  public setCurrentLocale(locale: string): string {
    // set local storage locale or locale cookie
    localStorage.setItem(this._lsLanguageCodeKey, locale);
    this.currentLocale = locale;
    return locale;
  }
  //#endregion

  //#region getLatestTranslationVersion
  /**
   * A method to get the current latest translation version from local storage
   * @returns string
   */
  public getCurrentTranslationVersion(): string {
    const latestTranslationVersion: string = localStorage.getItem(
      this._lsCurrentTranslationVersionKey,
    );
    const safeTranslationVersion: string =
      !['undefined', 'null'].includes(latestTranslationVersion) &&
      latestTranslationVersion !== null &&
      latestTranslationVersion !== undefined
        ? latestTranslationVersion
        : new Date(0).toISOString();
    this._translationVersion = safeTranslationVersion;
    return latestTranslationVersion;
  }
  //#endregion

  //#region setLatestTranslationVersion
  /**
   * A method to set the current latest translation version in local storage
   * @returns void
   */
  public setCurrentTranslationVersion(latestTranslationVersion: string): void {
    localStorage.setItem(
      this._lsCurrentTranslationVersionKey,
      latestTranslationVersion,
    );
    this._translationVersion = latestTranslationVersion;
  }
  //#endregion

  //#region setLocalDictionary
  /**
   * A method to set the local translation dictionary on local storage
   * @returns void
   */
  public setLocalDictionary(
    translations: Record<string, string>,
    updatedAt: string,
  ): void {
    // if (translations) { // commented out to ensure dictionary can be removed
    const translationDictionaryKey: string =
      this._genlocalStorageTranslationsKey();
    localStorage.setItem(
      translationDictionaryKey,
      JSON.stringify(translations),
    );
    this.translationDictionary = translations;
    this.setCurrentTranslationVersion(updatedAt);
  }
  //#endregion

  //#region setLocalDictionary
  /**
   * A method to set the local translation dictionary on local storage
   * @returns void
   *
   * Todo:
   * - Add method to remove items
   */
  public updateLocalDictionary(
    translations: Record<string, string>,
    updatedAt: string,
  ): void {
    // if (translations) { // commented out to ensure dictionary can be removed
    const translationDictionaryKey: string =
      this._genlocalStorageTranslationsKey();
    const oldDictionary: Record<string, string> = this.translationDictionary;
    const newDictionary: Record<string, string> = {
      ...oldDictionary,
      ...translations,
    };
    localStorage.setItem(
      translationDictionaryKey,
      JSON.stringify(newDictionary),
    );
    this.translationDictionary = newDictionary;

    this.setCurrentTranslationVersion(updatedAt);
  }
  //#endregion

  //#region _getLocalDictionary
  /**
   * A method to get the local translation dictionary from local storage
   * @returns boolean indicating if dictionary was present
   */
  private _getLocalDictionary(): boolean {
    // fetch page translations
    const translationSetKey: string = this._genlocalStorageTranslationsKey();
    const cachedTranslations: string | null =
      localStorage.getItem(translationSetKey);
    if (cachedTranslations) {
      this.translationDictionary = JSON.parse(cachedTranslations);
      // return true to indicate that cached translations were found
      return true;
    }
    // set to empty object to avoid type errors
    this.translationDictionary = {};
    // return false to indicate that no cached translations were found
    return false;
  }
  //#endregion

  //#region _genlocalStorageTranslationsKey
  /**
   * A method to generate the local storage translations key
   * @returns the local storage key for the specific translation dictionary
   */
  private _genlocalStorageTranslationsKey(): string {
    // const pageKey = this._getPageKey();
    // const localStorageKey = pageKey + '-' + this.currentLocale;
    // return localStorageKey;
    return this.currentLocale;
  }
  //#endregion

  //#region Observer
  private observeDOM(): void {
    const ovbserverTarget: Element = document.querySelector('ag-root');
    if (!ovbserverTarget) {
      console.error(
        'TranslatorService: Could not find ag-root element for observation',
      );
      return;
    }

    const observer: MutationObserver = new MutationObserver(() => {
      observerFunction();
    });

    const observerFunction = () => {
      if (this.currentLocale == 'en') return;
      // console.log('TranslatorService: DOM changed. Updating translations...');
      this.translate();
    };

    observer.observe(ovbserverTarget, {
      childList: true,
      subtree: true,
      // characterData: true,
      // attributes: false,
    });
  }
  //#endregion

  //#region Translate
  /**
   * A method to action page translation.
   * @returns void
   *
   * Steps:
   * - Fetch translatable nodes
   * - Translate nodes and collect nodes with no translations
   * - Build/create new translations
   * - Apply next translations if any
   */
  private async translate(): Promise<void> {
    if (this.currentLocale == 'en' || !this.isAgrigateUser()) {
      // remove if not needed elsewhere
      return;
    }
    this._showLoadingWidget('Translating...');
    this.isTranslating = true;

    const { nodes, nodeText }: { nodes: Node[]; nodeText: string[] } =
      this._getTranslatableNodes(document.body);
    const untranslatedText: string[] = [];
    const untranslatedNodes: Node[] = [];
    // for each translatable node, translate or add to list to be translated
    for (let i: number = 0; i < nodes.length; i++) {
      // find translation
      const translation: string | null = this.getLocalTranslation(nodeText[i]);
      if (translation == null) {
        // add to list to be translated if no local translation
        untranslatedText.push(nodeText[i]);
        untranslatedNodes.push(nodes[i]);
        // skip apply translation and go to next node
        continue;
      }
      // apply translation
      this._applyTranslation(nodes[i], translation);
    }

    this.isTranslating = false;
    if (!this.isTranslating && !this.isBuildingTranslations) {
      this._hideLoadingWidget();
    }

    if (untranslatedText.length > 0) {
      // build translations to be added
      try {
        if (this.isBuildingTranslations) {
          // console.count('multiple translator builders running');
        }
        this._showLoadingWidget('Building translations...');
        this.isBuildingTranslations = true;
        this.translationRequest$.next(untranslatedText);
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error creating translations:', error);
      }
    }
  }
  //#endregion

  //#region  Get Translatable nodes
  /**
   * A method to fetch all translatable nodes.
   *
   * View 'TranslatableNodeFilter' for details on how nodes are filtered
   *
   * @returns \{ nodes: translatable Nodes array, nodeText: string array }
   *
   */
  private _getTranslatableNodes(element: HTMLElement): {
    nodes: Node[];
    nodeText: string[];
    // add other text like 'hint', etc.
  } {
    // define return variables
    const nodes: Node[] = [];
    const nodeText: string[] = [];
    // create dom tree walker
    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      { acceptNode: (node) => this._translatableNodeFilter(node) },
    );

    // walk through dom tree and find translatable nodes
    let node: Node;
    while ((node = walker.nextNode())) {
      const text = node.nodeValue?.trim();
      nodes.push(node);
      nodeText.push(text);
    }

    return { nodes, nodeText };
  }
  //#endregion

  //#region Get Local Translation
  private getLocalTranslation(text: string): string | null {
    const key: string = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    //text.replace(/\s+/g, '_');
    const translation: string | null = this.translationDictionary[key];
    if (translation) {
      return translation;
    }
    return null;
  }
  //#endregion

  //#region Apply Translation
  private _applyTranslation(node: Node, translation: string): void {
    const parentElement = node.parentNode as Element;
    // apply translation
    this.renderer.setValue(node, translation);
    // mark parent element as translated
    this.renderer.setAttribute(parentElement, this._nodeTranslated, 'true');
  }
  //#endregion

  //#region Translatable Node Filter
  /**
   * A method to filter nodes as translatable.
   *
   * This excludes:
   * - Already translated nodes
   * - Nodes set to never be translated
   * - Nodes with a tag specified in '_excludeTags'
   * - Nodes and their children with a tag specified in '_excludeTagNodeChildren'
   *
   * @returns Node filter response
   */
  private _translatableNodeFilter(node: Node) {
    // Get some node info
    const text = node.nodeValue?.trim();
    const p = node.parentNode as Element;
    // Skip node, do not look deeper
    if (
      p?.getAttribute(this._doNotTranslate) === 'true' ||
      this._checkAnyTag(p, this.excludeTagNodeChildren)
    ) {
      return NodeFilter.FILTER_REJECT;
    }
    // Skip node, look deeper
    if (
      !text ||
      p?.getAttribute(this._nodeTranslated) === 'true' ||
      this._checkAnyTag(p, this.excludeTags) ||
      /^\d+(\.\d+)?$/.test(text)
    ) {
      return NodeFilter.FILTER_SKIP;
    }
    // Accept node
    return NodeFilter.FILTER_ACCEPT;
  }
  //#endregion

  //#region Tag checks
  private _checkTag(element: Element, tag: string): boolean {
    return element?.tagName.toLowerCase() === tag.toLowerCase();
  }

  private _checkAnyTag(element: Element, tags: string[]): boolean {
    for (let i: number = 0; i < tags.length; i++) {
      if (this._checkTag(element, tags[i])) {
        return true;
      }
    }
    return false;
  }
  //#endregion

  private _showLoadingWidget(message: string): void {
    let widget: Element = document.getElementById(this._translationWidgetId);
    if (!widget) {
      widget = this.renderer.createElement('div');
      this.renderer.setProperty(widget, 'id', this._translationWidgetId);
      // Apply styles with renderer
      this.renderer.setStyle(widget, 'position', 'fixed');
      this.renderer.setStyle(widget, 'bottom', '24px');
      this.renderer.setStyle(widget, 'right', '24px');
      this.renderer.setStyle(widget, 'background', 'rgba(27, 47, 70, 0.7)');
      this.renderer.setStyle(widget, 'color', 'white');
      this.renderer.setStyle(widget, 'padding', '10px');
      this.renderer.setStyle(widget, 'border-radius', '5px');
      this.renderer.setStyle(widget, 'z-index', '9999');
      this.renderer.setStyle(widget, 'opacity', '1');
      this.renderer.setStyle(widget, 'pointer-events', 'none');
      this.renderer.setStyle(widget, 'transition', 'none');

      // Set do-not-translate attribute
      this.renderer.setAttribute(widget, this._doNotTranslate, 'true');

      // Append to body
      this.renderer.appendChild(this.document.body, widget);
    }
    // Set message and show widget
    this.renderer.setProperty(widget, 'textContent', message);
    this.renderer.setStyle(widget, 'opacity', '1');
    this.renderer.setStyle(widget, 'pointer-events', 'auto');
  }

  private _hideLoadingWidget(): void {
    const widget: Element = document.getElementById(this._translationWidgetId);
    // if (widget) widget.remove();
    if (widget) {
      this.renderer.setStyle(widget, 'transition', 'opacity 3.0s ease-out');
      this.renderer.setStyle(widget, 'opacity', '0');
      this.renderer.setStyle(widget, 'pointer-events', 'none');
    }
  }
}
